import { useQuery } from "@tanstack/react-query";
import { USER_DOMAINS } from "../../constants/QueryKeys";
import UserService from "../../service/UserService";
import { queryClient, STORE_CACHE_DEFAULTS } from "../../utils/QueryUtils";
import { getUserId } from "./useAccount";

/**
 * @typedef {import("@/types/data/Domain.mjs").Domain} Domain
 * @typedef {{ domains: Domain[] }} UserDomainsData
 */
/**
 * @template T
 * @typedef {import("@/types/TypeUtils.mjs").QueryResultRest<T>} QueryResultRest
 */
/**
 * @template T
 * @typedef {import("@/types/TypeUtils.mjs").QueryHookOptions<T>} QueryHookOptions
 */

const defaults = STORE_CACHE_DEFAULTS;

// Registered on the shared client so DomainManagementStore's fetchQuery
// inherits these instead of the global retry/backoff defaults.
queryClient.setQueryDefaults([USER_DOMAINS], defaults);

/**
 * Loads the current user's domains (TanStack Query replacement for UserDomainsStore's `domains` list).
 * @param {QueryHookOptions<UserDomainsData>} [options] - Extra `useQuery` options, spread over the defaults.
 * @returns {{ domains: Domain[] | undefined } & QueryResultRest<UserDomainsData>}
 */
const useUserDomains = (options = {}) => {
  const { data, ...rest } = useQuery({
    queryKey: [USER_DOMAINS],
    queryFn: () => /** @type {Promise<UserDomainsData>} */ (UserService.loadDomains(getUserId())),
    ...defaults,
    ...options,
  });
  return { domains: data?.domains, ...rest };
};

/**
 * Pure selector: the account's primary domain is the one with no parent.
 * @param {Domain[]} [domains]
 * @returns {Domain | undefined}
 */
export const getPrimaryDomain = (domains) =>
  (domains || []).find((domain) => domain.parent_site_id === null);

/**
 * Pure selector: finds a domain by its site_id.
 * @param {Domain[]} [domains]
 * @param {number} siteId
 * @returns {Domain | undefined}
 */
export const getDomainBySiteId = (domains, siteId) =>
  (domains || []).find((domain) => domain.site_id === siteId);

/**
 * One-shot, non-triggering read of the cached domains payload for non-React callers (Reflux stores).
 * @returns {UserDomainsData | undefined}
 */
export const getUserDomainsData = () =>
  /** @type {UserDomainsData | undefined} */ (queryClient.getQueryData([USER_DOMAINS]));

/**
 * Writes the USER_DOMAINS cache entry for non-React callers.
 * @param {(prev: UserDomainsData | undefined) => UserDomainsData} updater
 */
export const setUserDomainsData = (updater) => queryClient.setQueryData([USER_DOMAINS], updater);

/**
 * Appends a domain to the cache.
 * @param {Domain} domain
 */
export const addDomainToCache = (domain) =>
  setUserDomainsData((prev) => ({
    ...prev,
    domains: [...(prev?.domains ?? []), domain],
  }));

/**
 * Rewrites the cached domain with the given site_id; no-op on an empty cache.
 * @param {number} siteId
 * @param {(domain: Domain) => Domain} update
 */
export const updateDomainInCache = (siteId, update) =>
  setUserDomainsData((prev) => {
    if (!prev) return prev;
    return {
      ...prev,
      domains: prev.domains.map((domain) => (domain.site_id === siteId ? update(domain) : domain)),
    };
  });

/**
 * Syncs a site's domain assignment into the cache: clears
 * `site_design_id`/`design_name` off whichever domain used to point at this
 * site design, then sets them on the domain now assigned to it (if any).
 * @param {{site_design_id: number, name: string, site_domain_id: number | null}} site
 */
export const applySiteDomainAssignment = ({ site_design_id, name, site_domain_id }) =>
  setUserDomainsData((prev) => {
    if (!prev) return prev;

    const domains = prev.domains.map((domain) => {
      if (domain.site_design_id == site_design_id) {
        domain = { ...domain, site_design_id: null, design_name: null };
      }

      if (domain.site_id == site_domain_id && site_domain_id != null) {
        domain = { ...domain, site_design_id, design_name: name };
      }

      return domain;
    });

    return { ...prev, domains };
  });

/** Fetches (or reuses the fresh cache of) the user's domains via the registered defaults. */
export const fetchUserDomains = () =>
  queryClient.fetchQuery({
    queryKey: [USER_DOMAINS],
    queryFn: () => /** @type {Promise<UserDomainsData>} */ (UserService.loadDomains(getUserId())),
  });

export default useUserDomains;
